import { useState } from 'react';
import classnames from 'classnames';
import './DatasetTypePicker.scss';
import { ReportDatatype } from '@/types/ReportRun';
import { useUser } from '@/contexts/UserContext';
import { ByzzerTipIcon } from '@byzzer/ui-components';

export type DatasetTypePickerProps = {
    value: ReportDatatype;
    onChange: (dataset: ReportDatatype) => void;
};

const baseClassName = 'dataset-type-picker';

const datasetOptions: { label: string; value: ReportDatatype }[] = [
    { label: 'RMS', value: 'rms' },
    { label: 'CPS', value: 'cps' },
    { label: 'Omnisales', value: 'omni' },
    { label: 'Omnishopper', value: 'osh' },
];

export const DatasetTypePicker = ({ value, onChange }: DatasetTypePickerProps) => {
    const { features } = useUser();
    const [expanded, setExpanded] = useState<boolean>(true);

    const handleGroupToggle = () => {
        setExpanded(!expanded);
    };

    const isOptionDisabled = (dataset: ReportDatatype): boolean => {
        if (dataset === 'omni' && !features?.enableOmniAccess) {
            return true;
        }
        if (dataset === 'osh' && !features?.enableOmniShopperAccess) {
            return true;
        }
        return false;
    };

    const getDisabledTipMessage = (dataset: ReportDatatype): string => {
        if (dataset === 'omni') {
            return 'You do not have access to Omnisales reports. Contact your NIQ Representative to purchase.';
        }
        if (dataset === 'osh') {
            return 'You do not have access to Omnishopper reports. Contact your NIQ Representative to purchase.';
        }
        return '';
    };

    const handleChange = (dataset: ReportDatatype) => {
        if (isOptionDisabled(dataset)) {
            return;
        }
        onChange(dataset);
    }

    return (
        <div className={classnames(`${baseClassName}`)}>
            <header className={classnames(`${baseClassName}__header`)}>
                <span className={classnames(`${baseClassName}__name`)}>
                    Your Default Selection
                </span>
            </header>

            <div
                className={classnames(`${baseClassName}__section-node`, {
                    [`${baseClassName}__section-node--expanded`]: expanded,
                })}
                onClick={handleGroupToggle}
            >
                Dataset
            </div>
            {expanded && (
                <div className={classnames(`${baseClassName}__options`)}>
                    {datasetOptions.map((option) => (
                        <div
                            key={option.value}
                            className={classnames(`${baseClassName}__node`, `${baseClassName}__value-node`, {
                                [`${baseClassName}__value-node--selected`]: value === option.value,
                                [`${baseClassName}__value-node--disabled`]: isOptionDisabled(option.value),
                            })}
                            onClick={() => handleChange(option.value)}
                        >
                            <span>{option.label}</span>
                            {isOptionDisabled(option.value) && (
                                <span className={`${baseClassName}__icon-wrapper`}>
                                    <ByzzerTipIcon
                                        className={`${baseClassName}__unpurchased-tip-icon`}
                                        tipDelay={[300, 0]}
                                        type={'unpurchased'}
                                        tip={getDisabledTipMessage(option.value)}
                                    />
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DatasetTypePicker;
